import { useOrganization } from '@/contexts/OrganizationContext';
import { getCurrencyLocale, resolveCurrency } from '@/lib/currency';

interface CurrencyDisplayProps {
  value: number;
  /** Account with optional currency override (takes precedence over org currency) */
  account?: { currency?: string | null } | null;
  /** Explicit currency override (takes precedence over account and org) */
  currency?: string;
  /** Number of decimal places (default: 2) */
  decimals?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Display a formatted currency amount using the appropriate currency settings.
 * Priority: explicit currency > account.currency > organization.currency > 'EUR'
 *
 * @example
 * // Uses organization currency
 * <CurrencyDisplay value={1234.56} />
 * // For EUR org: "€1.234,56"
 * // For USD org: "$1,234.56"
 *
 * // Uses account currency if set, falls back to org currency
 * <CurrencyDisplay value={1234.56} account={account} />
 *
 * // Explicit currency override
 * <CurrencyDisplay value={1234.56} currency="GBP" />
 * // "£1,234.56"
 */
export function CurrencyDisplay({
  value,
  account,
  currency,
  decimals = 2,
  className = ''
}: CurrencyDisplayProps) {
  const { selectedOrganization } = useOrganization();

  // Priority: explicit currency > account.currency > organization.currency > 'EUR'.
  // resolveCurrency trims/uppercases and skips blank codes (CSV bank imports
  // land ''), so a dirty currency degrades to the next source instead of
  // crashing Intl.NumberFormat with "Invalid currency code".
  const currencyCode = resolveCurrency([currency, account?.currency, selectedOrganization?.currency]);
  const locale = getCurrencyLocale(currencyCode);

  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

  return (
    <span className={className}>
      {formatted}
    </span>
  );
}